import { Injectable } from '@angular/core';
import { Observable, of, delay, tap } from 'rxjs';
import { PackageService } from './package.service';
import { Package } from '../../interfaces/package/package.interface';
import { PackageSearchParam } from '../../interfaces/package/package-search-param.interface';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';

@Injectable({
    providedIn: 'root'
})
export class PackageMockService extends PackageService {

    private mockPackages: Package[] = [
        {
            id: 'pkg-1',
            uniqueId: 'PKG001',
            industry: 'IT Services',
            packageName: 'Full Stack Web Development',
            jobRole: 'Full Stack Developer',
            category: 'Technology',
            description: 'Comprehensive program covering frontend and backend technologies including Angular, Node.js, and databases.',
            skills: ['Angular', 'Node.js', 'Express', 'MongoDB', 'TypeScript'],
            duration: 120, // in hours
            price: 50000,
            discountPercent: 10,
            status: 'Active',
            createdAt: '2023-10-01T10:00:00Z',
            modules: [
                { id: 'mod-1', moduleName: 'Frontend Fundamentals', duration: '40 hours' },
                { id: 'mod-2', moduleName: 'Backend Development', duration: '60 hours' },
                { id: 'mod-3', moduleName: 'Project & Deployment', duration: '20 hours' }
            ]
        },
        {
            id: 'pkg-2',
            uniqueId: 'PKG002',
            industry: 'Data Science',
            packageName: 'Data Analytics Mastery',
            jobRole: 'Data Analyst',
            category: 'Data Science',
            description: 'Learn data analysis, visualization, and basic machine learning concepts using Python and SQL.',
            skills: ['Python', 'SQL', 'Tableau', 'Pandas', 'Statistics'],
            duration: 90,
            price: 45000,
            discountPercent: 15,
            status: 'Active',
            createdAt: '2023-11-15T09:30:00Z',
            modules: [
                { id: 'mod-4', moduleName: 'Data Analysis with Python', duration: '40 hours' },
                { id: 'mod-5', moduleName: 'SQL & Databases', duration: '20 hours' },
                { id: 'mod-6', moduleName: 'Data Visualization', duration: '30 hours' }
            ]
        }
    ];

    constructor() {
        super();
    }

    override findAll(searchParam: PackageSearchParam): Observable<PageResponse<Package>> {
        let filtered = [...this.mockPackages];
        
        if (searchParam.searchTerm) {
            const term = searchParam.searchTerm.toLowerCase();
            filtered = filtered.filter(p => 
                p.packageName.toLowerCase().includes(term) || 
                p.category.toLowerCase().includes(term)
            );
        }

        return this.wrapRequest(
            of({
                content: filtered,
                page: {
                    number: searchParam.page || 0,
                    size: searchParam.size || 10,
                    totalElements: filtered.length,
                    totalPages: Math.ceil(filtered.length / (searchParam.size || 10))
                }
            }).pipe(delay(1000)),
            false,
            'Failed to fetch packages'
        );
    }

    override find(id: string): Observable<Package> {
        const pkg = this.mockPackages.find(p => p.id === id || p.uniqueId === id);
        return this.wrapRequest(
            of(pkg!).pipe(delay(1000)),
            false,
            'Failed to fetch package'
        );
    }

    override create(data: Partial<Package>): Observable<Package> {
        const newPkg = { 
            ...data, 
            id: 'pkg-' + Math.random().toString(36).substring(7),
            uniqueId: 'PKG' + Math.floor(Math.random() * 1000).toString().padStart(3, '0'),
            createdAt: new Date().toISOString(),
            status: 'Active'
        } as Package;
        
        this.mockPackages.push(newPkg);

        return this.wrapRequest(
            of(newPkg).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('Package created successfully');
            })),
            false,
            'Failed to create package'
        );
    }

    override update(id: string, data: Partial<Package>): Observable<Package> {
        const index = this.mockPackages.findIndex(p => p.id === id || p.uniqueId === id);
        if (index !== -1) {
            this.mockPackages[index] = { ...this.mockPackages[index], ...data, updatedAt: new Date().toISOString() } as Package;
        }
        return this.wrapRequest(
            of(this.mockPackages[index]).pipe(delay(1500)).pipe(tap(() => {
                this.toast.success('Package updated successfully');
            })),
            false,
            'Failed to update package'
        );
    }

    override delete(id: string): Observable<void> {
        this.mockPackages = this.mockPackages.filter(p => p.id !== id && p.uniqueId !== id);
        return this.wrapRequest(
            of(undefined).pipe(delay(1000)).pipe(tap(() => {
                this.toast.success('Package deleted successfully');
            })),
            false,
            'Failed to delete package'
        );
    }
}
